import styled, { css } from 'styled-components';
import { media, layout, colors } from '@prisjakt/ui-base';
import { Card as CardComponent, CardActionArea } from '@prisjakt/ui/card';
import { BodySmallText } from '@prisjakt/ui/text/variants';
import PriceNotice from '../components/price-notice';
import StockStatusColumnComponent from './regular-old/components-old/stock-status-column';
import { ITableLinkProps } from '../types';

export const MobileOnly = styled.div`
  ${media.mediumUp`
    display: none;
  `}
`;

export const DesktopAndMedium = styled.div`
  ${media.smallOnly`
    display: none;  
  `}
`;

export const Content = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

export const RowColumn = styled.div`
  display: flex;
  align-items: center;
  margin: 0 ${layout.measurements.spacingSmall};
`;

export const ActionArea = styled(CardActionArea)`
  flex: 1 1 auto;
  cursor: pointer;
`;

type ColumnProps = {
  expand?: boolean;
};

export const Column = styled.div<ColumnProps>(
  props => css`
    display: flex;
    align-items: stretch;

    > .pj-ui-popover--wrapper {
      display: block;
      width: 100%;
      height: 100%;
    }

    ${props.expand &&
      css`
        flex: 2;
      `}
  `
);

interface LinkProps extends ITableLinkProps {
  alignCenter?: boolean;
  alignLeft?: boolean;
  alignRight?: boolean;
  vertical?: boolean;
}

export const Link = styled.a<LinkProps>(
  props => css`
  
  display: flex;
  flex: 2;
  align-items: center;

  ${!props.featured &&
    css`
      cursor: default;
    `}

  ${props.alignCenter &&
    css`
      justify-content: center;
    `}
  ${props.alignLeft &&
    css`
      justify-content: flex-start;
    `}
  ${props.alignRight &&
    css`
      justify-content: flex-end;
    `}
    
    &:target {
      outline: ${colors.borderLightGray} dotted 1px;
      outline: -webkit-focus-ring-color auto 1px;
    }
`
);

type SharedRowProps = {
  noExternalUri?: boolean;
  featured?: boolean;
  showReport?: boolean;
  hoverEnabled?: boolean;
  showAsMobile?: boolean;
};

const rowStyles = css`
  line-height: 1.3em;
  position: relative;

  ${(props: SharedRowProps) =>
    props.noExternalUri &&
    css`
      cursor: default;
    `}

  ${props =>
    !props.featured &&
    css`
      color: ${props.theme.text.secondary};
      &:hover {
        color: ${props.theme.text.secondary};
      }
      background: ${props.theme.colors.base100};
    `}
`;

type RowExperimentProps = {
  expanded?: boolean;
};

export const LeftColumn = styled.div<SharedRowProps>(
  props => css`
    align-items: stretch;

    ${props.featured &&
      css`
        border-right: 1px solid ${props.theme.colors.base200};
      `}
  `
);

export const MiddleColumn = styled.div<SharedRowProps>(
  props => css`
    display: flex;
    flex: 1;
    flex-direction: column;
    height: 100%;
    width: 100%;
    ${(props.featured || props.hoverEnabled) &&
      css`
        &:hover::after {
          box-shadow: 0 2px 10px 0 rgb(0, 0, 0, 0.1), 0 0 0 1px rgb(0, 0, 0, 0);
        }
        ::after {
          transition: box-shadow 200ms ease;
          content: '';
          position: absolute;
          top: 0;
          bottom: 0;
          left: 0;
          right: 0;
          pointer-events: none;
          border-radius: 8px;
        }
      `}
  `
);

export const TopMiddleRow = styled.div<SharedRowProps & AlternativeRowProps>(
  props => css`
    display: flex;
    padding: ${layout.measurements.spacingSmall};
    width: 100%;
    height: 100%;
    align-items: center;
    justify-content: space-between;
    flex: 1;

    ${media.smallOnly`
      padding: ${layout.measurements.spacingTiny} 0;
      gap: ${layout.measurements.spacingSmall};
    `}
  `
);

type BottomMiddleRowProps = {
  hasStoreIndicators: boolean;
};

export const BottomMiddleRow = styled.div<
  SharedRowProps & BottomMiddleRowProps
>(
  props => css`
    display: ${props.hasStoreIndicators ? 'flex' : 'none'};
    padding: ${layout.measurements.spacingSmall};
    width: 100%;
    height: 100%;
    align-items: center;
    justify-content: space-between;

    ${props.featured &&
      css`
        border-top: 1px solid ${props.theme.colors.base200};
        flex: 0;
      `}

    ${media.smallOnly`
      display: flex;
    `}
  `
);

export const EndColumn = styled.div<SharedRowProps>(
  props => css`
    display: flex;
    align-items: stretch;
    height: 100%;
    padding: ${layout.measurements.spacingSmall};

    ${media.largeUp`
      padding-right: ${layout.measurements.spacingNormal};
    `}

    ${!props.featured &&
      css`
        visibility: hidden;
      `}
  `
);

export const RowCard = styled(CardComponent)<
  SharedRowProps & RowExperimentProps
>(
  props => css`
    ${rowStyles}
    margin-bottom: ${layout.measurements.spacingSmall};
    ${!props.featured &&
      !props.expanded &&
      css`
        box-shadow: none;
      `}
  `
);

export const Row = styled.div<SharedRowProps & RowExperimentProps>(
  props => css`
    ${rowStyles}
    border-radius: ${props.expanded ? '8px 8px 0 0' : '8px'};
    display: flex;    
    align-items: stretch;

    ${!props.featured &&
      css`
        height: 54px;
      `}

    ${props.expanded &&
      css`
        border-bottom: 1px solid ${props.theme.colors.base200};
      `}
  `
);

export const InfoRow = styled.div`
  background: ${props => props.theme.colors.base100};
  background-color: rgba(0, 0, 0, 0.03);
`;

export const AlternativePricesArea = styled.div<SharedRowProps>(
  props => css`
    background: ${props.theme.colors.base100};
    background-color: rgba(0, 0, 0, 0.03);
    padding: 0.75rem;
  `
);

export const AlternativePricesInner = styled.div<SharedRowProps>(
  props => css`
    background: ${props.featured
      ? props.theme.colors.base0
      : props.theme.colors.base100};

    border-radius: 4px;
    border: 1px solid ${props.theme.colors.base200};
  `
);

export const AltRow = styled.div<SharedRowProps>(
  props => css`
    display: flex;
    flex: 1;
    &:hover {
      background-color: rgba(0, 0, 0, 0.05);
    }

    ${props.featured &&
      css`
        &:not(:last-child) {
          border-bottom: 1px solid ${props.theme.colors.base200};
        }
      `}
  `
);

type AlternativeRowProps = {
  isAlternative?: boolean;
};

export const ReportRow = styled.div`
  ${rowStyles}
  margin-bottom: ${layout.measurements.spacingSmall};
  padding: 0 ${layout.measurements.spacingNormal};
  border-radius: 8px;
`;

export const AltReportRow = styled.div`
  ${rowStyles}
  background-color: rgba(0, 0, 0, 0.03);
  padding: 0 ${layout.spacing.small};
`;

export const ProductName = styled(BodySmallText)<
  SharedRowProps & AlternativeRowProps
>(
  props => css`
    overflow: hidden;
    text-overflow: ellipsis;
    color: ${props.featured
      ? props.theme.text.primary
      : props.theme.text.tertiary};

    white-space: ${props.showAsMobile && 'nowrap'};

    ${media.smallOnly`
      white-space: nowrap;

      ${!props.isAlternative &&
        css`
          display: none;
        `}
  `};
  `
);

export const TransparencyShopInfo = styled.div`
  text-align: left;
  margin: 0;
`;

export const HasAlternativePricesInfo = styled.div<SharedRowProps>`
  padding: 2px ${layout.measurements.spacingSmall};
  margin-left: ${layout.measurements.spacingSmall};

  ${props =>
    props.featured &&
    css`
      background-color: ${props.theme.colors.base100};
      border-radius: 2px;
    `}

  ${media.smallOnly`
    display: none;  
  `};
`;

export const StoreInfoMobileView = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${layout.measurements.spacingSmall};
  margin-right: ${layout.measurements.spacingSmall};
  align-items: center;
`;

export const PaymentProvidersColumn = styled.div`
  display: flex;
  gap: ${layout.measurements.spacingSmall};
  align-items: center;
  filter: grayscale(1);
  opacity: 0.6;

  ${media.smallOnly`
    & > img {
      height: ${layout.measurements.fontSizeSmall};
    }
  `};
`;

export const GoToStoreColumn = styled.div<SharedRowProps & AlternativeRowProps>(
  props => css`
    ${props.featured &&
      css`
        display: flex;
        align-self: center;
        justify-content: center;
        width: ${props.isAlternative ? 32 : 40}px;
        padding-right: ${props.isAlternative ? 8 : 0}px;
      `}

    ${!props.featured &&
      css`
        visibility: hidden;
      `}
  `
);

export const ProductNameColumn = styled.div<SharedRowProps>(
  props => css`
    display: flex;
    align-items: center;
    flex: 0 1 auto;

    ${media.mediumUp`
      max-width: 65%;
    `}

    ${media.smallOnly`
      max-width: 60%;
      display: grid;
    `}
  `
);

export const PriceNoticeLabel = styled(PriceNotice)`
  font-size: ${layout.measurements.fontSizeSmall};
  white-space: nowrap;
  font-style: italic;
  margin-bottom: 2px;
`;

const sharedPriceColumnStyles = css`
  display: flex;
  align-items: center;
  height: 24px;
`;

export const StockStatusColumn = styled(StockStatusColumnComponent)`
  ${sharedPriceColumnStyles};
`;

export const PriceLabelWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${layout.measurements.spacingSmall};
  align-items: flex-end;
`;

export const BadgeWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  gap: ${layout.measurements.spacingSmall};
`;
